import ShapeBase from './ShapeBase';
import {
  computeRoundedCornerPath,
  makeSimplePath,
  parsePath,
  type CoordsArray,
} from '.';
import { type IPoint } from 'fabric/fabric-impl';
import type Shape from '../objects/Shape';
import { pathDataToPath } from './utils';

// Shapes that are simple polygons are defined using the SVG path syntax.
//
// This is so that in the future  we can both render the path and also generate
// a paper.js compound path (http://paperjs.org/reference/compoundpath/). With
// the compound path we can do automatic point on path detection and normal
// computation. This will allow connectors that come from the center of the
// shape.
class PolygonBase extends ShapeBase {
  static width: number;
  static height: number;
  static pathData: CoordsArray[];
  static edgePathData?: CoordsArray[];
  static pathStrokeOnly = false;
  booleanOperationCompatible = true;

  /**
   * Return the SVG path of the shape.
   * Is called every time it needs to redraw, and will not be cached.
   * Only define if the path needs rebuilding when resized.
   */
  static dynamicPathString?: (scaleX: number, scaleY: number) => string;
  /** Return the SVG path of the shape. Will be called once and cached. */
  static pathString?: () => string;
  /** Override to provide a custom edge path for connector attachment. Should be clockwise. */
  static edgePathString?: () => string;

  constructor(fabricObject: Shape) {
    super(fabricObject);

    const constructor = this.constructor as typeof PolygonBase;

    constructor.width ||= ShapeBase.defaultWidth();
    constructor.height ||= ShapeBase.defaultHeight();

    // Parse the path just once for all instances.
    if (constructor.pathString) {
      constructor.pathData ||= makeSimplePath(constructor.pathString());
    }
    if (constructor.edgePathString) {
      constructor.edgePathData ||= makeSimplePath(constructor.edgePathString());
    }
  }

  static defaultWidth() {
    return this.width || ShapeBase.defaultWidth();
  }

  static defaultHeight() {
    return this.height || ShapeBase.defaultWidth();
  }

  // Compute the width & height of this type of shape based on an array of
  // points normalized to a unit size.
  static dimensionsFromPoints(points) {
    let minX = Number.MAX_VALUE;
    let minY = Number.MAX_VALUE;
    let maxX = Number.MIN_VALUE;
    let maxY = Number.MIN_VALUE;

    for (let i = 0; i < points.length; i++) {
      minX = Math.min(minX, points[i].x);
      minY = Math.min(minY, points[i].y);
      maxX = Math.max(maxX, points[i].x);
      maxY = Math.max(maxY, points[i].y);
    }
    this.width = (maxX - minX) * ShapeBase.defaultWidth();
    this.height = (maxY - minY) * ShapeBase.defaultHeight();
  }

  pathData() {
    const constructor = this.constructor as typeof PolygonBase;
    if (constructor.dynamicPathString) {
      return parsePath(constructor.dynamicPathString(this.scaleX, this.scaleY));
    } else {
      return constructor.pathData;
    }
  }

  paperPath(): paper.Path {
    const pathData = this.pathData();
    return pathDataToPath(pathData, this.fabricObject, this.width, this.height);
  }

  edgePaperPath(): paper.Path | undefined {
    const constructor = this.constructor as typeof PolygonBase;
    const pathData = constructor.edgePathData;
    if (!pathData) {
      return;
    }
    return pathDataToPath(pathData, this.fabricObject, this.width, this.height);
  }

  renderPath(ctx: CanvasRenderingContext2D, clip = false) {
    const pathData = this.pathData();
    const obj = this.fabricObject;
    let current: CoordsArray; // current instruction
    const w = this.width;
    const h = this.height;
    let x = 0;
    let y = 0;

    ctx.beginPath();
    for (let i = 0, len = pathData.length; i < len; ++i) {
      current = pathData[i];

      switch (current[0]) {
        case 'L': // lineto, absolute
          x = current[1];
          y = current[2];
          ctx.lineTo(x * w, y * h);
          break;

        case 'M': // moveTo, absolute
          x = current[1];
          y = current[2];
          ctx.moveTo(x * w, y * h);
          break;

        case 'C': // bezierCurveTo, absolute
          x = current[5];
          y = current[6];
          ctx.bezierCurveTo(
            current[1] * w,
            current[2] * h,
            current[3] * w,
            current[4] * h,
            current[5] * w,
            current[6] * h
          );
          break;

        case 'Q': // quadraticCurveTo, absolute
          x = current[3];
          y = current[4];
          ctx.quadraticCurveTo(
            current[1] * w,
            current[2] * h,
            current[3] * w,
            current[4] * h
          );
          break;

        case 'R': {
          const [aX, aY, bX, bY, cX, cY, dX, dY, eX, eY] =
            computeRoundedCornerPath(
              obj,
              x,
              y,
              current[1],
              current[2],
              current[3]
            );

          ctx.lineTo(aX, aY);
          ctx.bezierCurveTo(bX, bY, cX, cY, dX, dY);
          ctx.lineTo(eX, eY);
          x = eX;
          y = eY;
          break;
        }

        case 'z':
        case 'Z':
          if (clip) {
            ctx.clip();
          } else {
            ctx.closePath();
          }
          break;
      }
    }
  }

  render(ctx: CanvasRenderingContext2D) {
    const obj = this.fabricObject;

    this.renderPath(ctx);
    this._setBorderDash(ctx);

    if ((this.constructor as typeof PolygonBase).pathStrokeOnly) {
      obj._renderStroke(ctx);
    } else {
      if (obj.strokeDouble) {
        // for "double" borders, render a line the same color as the background in the middle of
        // the actual border, 1/3 its width, to give the appearance of two lines

        obj._renderFill(ctx);
        obj._renderStroke(ctx);

        ctx.save();
        if (obj.strokeUniform) {
          ctx.scale(1 / this.scaleX, 1 / this.scaleY);
        }
        ctx.setLineDash([]);
        ctx.lineWidth = obj.strokeWidth / 3;
        if (obj.fill && obj.fill !== 'transparent') {
          ctx.strokeStyle = obj.fill;
        } else {
          ctx.globalCompositeOperation = 'destination-out';
        }
        ctx.stroke();
        ctx.restore();
      } else {
        obj._renderPaintInOrder(ctx);
      }
    }
  }

  // Generate the vertices of a unit polygon with `numPoints` vertices, rotated by
  // `theta` radians.
  static polygonPoints(numPoints: number, theta: number, radius = 0.5) {
    const points: IPoint[] = [];

    for (let i = 1; i <= numPoints; i++) {
      points.push({
        x: radius * Math.cos((2 * Math.PI * i) / numPoints + theta),
        y: radius * Math.sin((2 * Math.PI * i) / numPoints + theta),
      });
    }

    return points;
  }
}

export default PolygonBase;
